import React, { useState } from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Tooltip
} from '@mui/material';
import {
  Person,
  PhotoCamera,
  ZoomIn,
  Close
} from '@mui/icons-material';

const PhotoThumbnail = ({
  photoUrl,
  visitorName,
  size = 40,
  variant = 'circular', // 'circular' or 'square'
  showZoom = true,
  showCamera = false,
  onPhotoCapture = null,
  sx = {}
}) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Handle photo preview
  const handlePreview = () => {
    if (photoUrl && showZoom && !imageError) {
      setOpenPreview(true);
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-block', ...sx }}>
        <Avatar
          src={photoUrl && !imageError ? photoUrl : undefined}
          alt={visitorName || 'Visitor'}
          variant={variant}
          sx={{
            width: size,
            height: size,
            bgcolor: photoUrl && !imageError ? 'transparent' : 'primary.main',
            color: 'white',
            fontSize: size * 0.4,
            cursor: photoUrl && showZoom && !imageError ? 'pointer' : 'default',
            border: '2px solid',
            borderColor: 'grey.300',
            '&:hover': {
              borderColor: photoUrl && showZoom && !imageError ? 'primary.main' : 'grey.300',
              transform: photoUrl && showZoom && !imageError ? 'scale(1.05)' : 'none',
            },
            transition: 'all 0.2s ease-in-out'
          }}
          onClick={handlePreview}
          onError={() => setImageError(true)}
        >
          {(!photoUrl || imageError) && <Person sx={{ fontSize: size * 0.6 }} />}
        </Avatar>

        {/* Camera icon for photo capture */}
        {showCamera && (
          <Tooltip title="Take Photo">
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                bgcolor: 'primary.main',
                color: 'white',
                width: size * 0.4,
                height: size * 0.4,
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
              onClick={onPhotoCapture}
            >
              <PhotoCamera sx={{ fontSize: size * 0.25 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Zoom icon overlay */}
        {photoUrl && showZoom && !imageError && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.3)',
              borderRadius: variant === 'circular' ? '50%' : '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              '&:hover': {
                opacity: 1,
              }
            }}
          >
            <ZoomIn sx={{ color: 'white', fontSize: size * 0.4 }} />
          </Box>
        )}
      </Box>

      {/* Photo Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {visitorName || 'Visitor Photo'}
          <IconButton onClick={() => setOpenPreview(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', p: 3 }}>
          {photoUrl && !imageError ? (
            <img
              src={photoUrl}
              alt={visitorName || 'Visitor'}
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
            />
          ) : (
            <Box
              sx={{
                width: 200,
                height: 200,
                bgcolor: 'grey.100',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto'
              }}
            >
              <Person sx={{ fontSize: 80, color: 'grey.400' }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PhotoThumbnail;
